import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EditOutlined, DownOutlined, CloseCircleOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import {
  Button, Col, Input, message, Row, Select, Spin,
  Form, notification,
} from 'antd';
import RefreshIcon from 'mdi-react/RefreshIcon';
import { namespaces } from '../../../../i18n/i18n.constants';
import { ServiceParams, ServiceTypeFromList } from '../../../../sharedTypes/ServiceTypes';
import { BranchCoopercitrusFromClient } from '../../../../sharedTypes/BranchTypes';
import { formatBP } from '../../../../utils/format';
import './SelectPropertyData.scss';
import { backendResponseCodes } from '../../../../utils/responseMessages';
import {
  get, put, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../../dataSource/requests';
import { getUserName, getUserType, getUserBP } from '../../../../utils/users';
import { SessionExpiredException } from '../../../../utils/errors';

const SelectPropertyData = (props): JSX.Element => {
  interface InternalMatriculation {
    error?: boolean;
    errorMessage?: string;
    status?: string;
    value?: string;
    cooperadoName?: string;
  }

  interface Land {
    id: number;
    location: {
      lat: number;
      lon: number;
    };
    name: string;
    registeredOn: string;
  }

  interface Property extends Land {
    matriculation: string;
  }

  interface MatriculationWithLand {
    id: string;
    lands: Array<Land>;
  }

  interface LandOwner {
    id: string;
    name: string;
    disabled?: boolean;
  }

  const {
    nextPage, params, previousPage, updateParams,
  }: {
    nextPage: (pa: any) => any,
    params: ServiceParams,
    previousPage: (pa: any) => any,
    updateParams: any
  } = props;

  const { t } = useTranslation('namespaces');
  const userType = getUserType();

  const [formInstance] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [cooperProperties, setCooperProperties] = useState<Array<Property>>([]);
  const [otherProperties, setOtherProperties] = useState<Array<Property>>([]);
  const [owners, setOwners] = useState<Array<LandOwner>>([]);
  const [, setForceRerender] = useState<number>(1);

  const myPropertiesRequest = useRef<RequestWrapper|undefined>(undefined);
  const matriculationsWithLandsRequest = useRef<RequestWrapper|undefined>(undefined);
  const branchesRequest = useRef<RequestWrapper|undefined>(undefined);
  const updateDataFromSAPRequest = useRef<RequestWrapper|undefined>(undefined);
  const retrieveLandOwnersByLandRequest = useRef<RequestWrapper|undefined>(undefined);
  const validateMatriculationRequest = useRef<RequestWrapper|undefined>(undefined);
  const subtypeDynamicFieldRequest = useRef<RequestWrapper|undefined>(undefined);
  const typeDynamicFieldRequest = useRef<RequestWrapper|undefined>(undefined);

  const userName = getUserName();

  // COOPERCITRUS: começa com o valor null e deve ser preenchido
  // COOPERATIVE: começa com o valor COOPERATIVE e o campo é ocultado
  const [
    selectedMatriculation,
    setSelectedMatriculation,
  ] = useState<string | null>(params.selectedMatriculation);

  const [selectedProperty, setSelectedProperty] = useState<Property | null | undefined>(null);
  const [selectedPropertyError, setSelectedPropertyError] = useState<boolean>(false);
  const [selectedOwner, setSelectedOwner] = useState<LandOwner | null | undefined>(null);
  const [validating, setValidating] = useState<boolean>(false);
  const [matriculation, setMatriculation] = useState<InternalMatriculation>({});
  const [
    selectedPropertyMatriculation,
    setSelectedPropertyMatriculation,
  ] = useState<string | null | undefined>(null);
  const [selectedType, setSelectedType] = useState<ServiceTypeFromList | null | undefined>(null);
  const [
    branchsCoopercitrus,
    setBranchsCoopercitrus,
  ] = useState<Array<BranchCoopercitrusFromClient>>([]);

  const getListAllBranchs = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: '/branch/listAllBranchCoopercitrus',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    if (branchesRequest.current) {
      branchesRequest.current.abort();
    }

    branchesRequest.current = requestReference;

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 13000):
        // "Branch coopercitrus listed successfully", 13000
        setBranchsCoopercitrus(response?.body.listAllBranchCoopercitrusOutputs);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Desculpe, você não possui permissão para executar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('error_getting_subsidiaries', { ns: namespaces.pages.service.serviceComponents.selectPropertyData.selectPropertyData }),
        });
        break;
    }
  };

  const refreshPropertiesPart2 = async (): Promise<void> => {
    let urlLands = '/customer/findMatriculationsWithLands';

    if (getUserType() === 'COOPERCITRUS') {
      urlLands = `/admin/findMatriculationsWithLands?bp=${selectedMatriculation}`;
    }

    setLoading(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError;

    const requestReference: RequestWrapper = get({
      url: urlLands,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    if (matriculationsWithLandsRequest.current) {
      matriculationsWithLandsRequest.current.abort();
    }

    matriculationsWithLandsRequest.current = requestReference;

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 2000):
      case (response?.body?.code === 2003): {
        const newCooperProperties: Array<Property> = [];
        const newOtherProperties: Array<Property> = [];
        // "Listed customer matriculations with lands successfully", 2000
        const { matriculations }: { matriculations: Array<MatriculationWithLand> } = response?.body;

        matriculations.forEach((v: MatriculationWithLand) => {
          v.lands.forEach((land: Land) => {
            if (land.registeredOn === 'SAP') {
              newCooperProperties.push({ ...land, matriculation: v.id });
            } else {
              newOtherProperties.push({ ...land, matriculation: v.id });
            }
          });
        });

        setCooperProperties(newCooperProperties);
        setOtherProperties(newOtherProperties);
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseError?.body?.code === -19000):
        // "SAPTimeOut", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Desculpe, houve um erro ao buscar as propriedades, por favor tente novamente mais tarde.',
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;
    }

    setLoading(false);
  };

  const refreshProperties = async (): Promise<void> => {
    let urlUpdate = '/service/updateDataFromSAP';
    let responseUpdate: ResponseWrapper | undefined;
    let responseUpdateError: ResponseWrapper | undefined;
    let genericError;

    if (getUserType() === 'COOPERCITRUS') {
      urlUpdate = `/service/updateDataFromSAP?bp=${selectedMatriculation}`;
    } else {
      const userBP = getUserBP();
      urlUpdate = `/service/updateDataFromSAP?bp=${userBP}`;
    }

    setLoading(true);

    const requestReference: RequestWrapper = put({
      url: urlUpdate,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    if (updateDataFromSAPRequest.current) {
      updateDataFromSAPRequest.current.abort();
    }

    updateDataFromSAPRequest.current = requestReference;

    try {
      responseUpdate = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseUpdateError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (responseUpdate?.body?.code === 5003):
        // "Data from SAP updated during service request successfully", 5003
        await refreshPropertiesPart2();
        break;

      case (responseUpdateError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -18001):
        // "Bp required", -18001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.BP
            .BpRequired.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -19000):
        // "SAPTimeOut", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -18000):
        // "Bp not found of SAP", -18000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.BP
            .BpNotFoundForSap.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -2000):
        // "Matriculation cannot be null", -2000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.Matriculation
            .MatriculationCannotBeNull.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -2005):
        // "There are no valid matriculations", -2005
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.Matriculation
            .ThereAreNoValidMatriculations.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (responseUpdateError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Desculpe, ocorreu um erro ao atualizar as propriedades, por favor tente novamente mais tarde.',
        });
        setCooperProperties([]);
        setOtherProperties([]);
        break;
    }

    setLoading(false);
  };

  const getOwners = async (property: number): Promise<void> => {
    formInstance.setFieldsValue({
      owner: undefined,
    });

    setSelectedOwner(null);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError;

    const requestReference: RequestWrapper = get({
      url: `/land/retrieveLandOwnersByLand?landId=${property}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    if (retrieveLandOwnersByLandRequest.current) {
      retrieveLandOwnersByLandRequest.current.abort();
    }

    retrieveLandOwnersByLandRequest.current = requestReference;

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 6002): {
        // "Land owners retrieved by land successfully", 6002
        const { landOwners }: { landOwners: Array<LandOwner> } = response?.body;
        landOwners.forEach((eachLandOwner) => {
          const newEachLandOwner = eachLandOwner;
          newEachLandOwner.disabled = true;
        });
        setOwners(landOwners);

        if (landOwners.length === 1) {
          const [newSelectedOwner] = landOwners;
          landOwners.forEach(
            (eachOwner) => {
              const newEachOwner = eachOwner;
              newEachOwner.disabled = false;
            },
          );
          setOwners(landOwners);
          formInstance.setFieldsValue({ owner: newSelectedOwner?.id });
          setSelectedOwner(newSelectedOwner);
          setForceRerender((previous) => previous + 1);
        } else {
          const userBP = getUserBP();
          const foundOwner = landOwners.find(
            (eachLandOwner) => {
              if (userType === 'COOPERATIVE') {
                return eachLandOwner.id === userBP;
              }

              return eachLandOwner.id === formInstance.getFieldValue('matricula');
            },
          );

          landOwners.forEach(
            (eachOwner) => {
              const newEachOwner = eachOwner;
              if (userType === 'COOPERATIVE' && eachOwner.id === userBP) {
                newEachOwner.disabled = false;
              }

              if (userType === 'COOPERCITRUS' && eachOwner.id === formInstance.getFieldValue('matricula')) {
                newEachOwner.disabled = false;
              }
            },
          );
          setOwners(landOwners);

          if (foundOwner) {
            formInstance.setFieldsValue({ owner: foundOwner.id });
            setSelectedOwner(foundOwner);
          }
        }
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6002):
        // "Land not found", -6002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.Land
            .LandNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        // 'Ocorreu um erro ao buscar as proprietários, tente novamente'
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Desculpe, houve um erro ao buscar os proprietários, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  const selectProperty = (property: number, arrayProperties: Array<Property>): void => {
    const newSelectedProperty = arrayProperties.find((v) => v.id === property);
    setSelectedProperty(newSelectedProperty);
    setSelectedPropertyMatriculation(newSelectedProperty?.matriculation);
    setSelectedPropertyError(false);
    getOwners(property);
  };

  const getMyProperties = async (): Promise<void> => {
    let url = '/customer/findMatriculationsWithLands';
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError;

    if (getUserType() === 'COOPERCITRUS') {
      url = `/admin/findMatriculationsWithLands?bp=${selectedMatriculation}`;
    }

    setLoading(true);

    const requestReference: RequestWrapper = get({
      url,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    if (myPropertiesRequest.current) {
      myPropertiesRequest.current.abort();
    }

    myPropertiesRequest.current = requestReference;

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 2000):
      case (response?.body?.code === 2003): {
        // "Listed matriculation with lands successfully", 2003
        const newCooperProperties: Array<Property> = [];
        const newOtherProperties: Array<Property> = [];

        const { matriculations }: {
          matriculations: Array<MatriculationWithLand>|undefined
        } = response?.body;

        matriculations?.forEach((v) => {
          v.lands.forEach((land) => {
            if (land.registeredOn === 'SAP') {
              newCooperProperties.push({ ...land, matriculation: v.id });
            } else {
              newOtherProperties.push({ ...land, matriculation: v.id });
            }
          });
        });

        if (params.createdLandId) {
          selectProperty(params.createdLandId, newOtherProperties);
        }

        setCooperProperties(newCooperProperties);
        setOtherProperties(newOtherProperties);
        setLoading(false);
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        setLoading(false);
        refreshProperties();
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        setLoading(false);
        refreshProperties();
        break;

      case (responseError?.body?.code === -19000):
        // "SAP timeout", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        setLoading(false);
        // refreshProperties();
        break;

      case (responseError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        setLoading(false);
        break;

      case (responseError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        setLoading(false);
        // refreshProperties();
        break;

      case (responseError?.body?.code === -2002):
        // "Matriculation not found", -2002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.Matriculation
            .MatriculationNotFound.description.pt_br,
        });
        setLoading(false);
        refreshProperties();
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Desculpe, houve um erro ao buscar as propriedades, '
            + 'por favor tente novamente mais tarde',
        });
        setLoading(false);
        refreshProperties();
        break;
    }
  };

  const getItemIcon = (statuss: string | undefined): JSX.Element | null => {
    switch (statuss) {
      case 'success':
        return (<CheckCircleOutlined />);

      case 'error':
        return (<CloseCircleOutlined />);

      default:
        return null;
    }
  };

  const selectOwner = (owner: string): void => {
    const newSelectedOwner = owners.find((v: LandOwner) => v.id === owner);
    setSelectedOwner(newSelectedOwner);
  };

  const validateMatriculation = async (value: string): Promise<void> => {
    let result: ResponseWrapper | undefined;
    let resultError: ResponseWrapper | undefined;
    let genericError;

    if (value.length < 10) {
      return;
    }

    const url = `/admin/validateBp?bp=${value}`;

    const requestReference: RequestWrapper = get({
      url,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    if (validateMatriculationRequest.current) {
      validateMatriculationRequest.current.abort();
    }

    validateMatriculationRequest.current = requestReference;

    try {
      result = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        resultError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (result?.body?.code === 17000):
        // "BP validated successfully", 17000
        matriculation.error = false;
        matriculation.errorMessage = '';
        matriculation.status = 'success';
        matriculation.value = value;
        matriculation.cooperadoName = result?.body.cooperadoName;
        setValidating(false);
        setMatriculation(matriculation);
        setSelectedMatriculation(matriculation.value);
        setSelectedPropertyError(false);

        if (params.branchCoopercitrusId === 0) {
          params.branchCoopercitrusId = formInstance.getFieldValue('branchCoopercitrus');
          params.branchName = branchsCoopercitrus.find((i) => {
            return i.id === formInstance.getFieldValue('branchCoopercitrus');
          })?.name;
        }
        break;

      case (resultError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (resultError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });

        matriculation.error = true;
        matriculation.errorMessage = backendResponseCodes.Error.General
          .Forbidden.description.pt_br;
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;

      case (resultError?.body?.code === -18000):
        // "Bp not found of SAP", -18000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.BP
            .BpNotFoundForSap.description.pt_br,
        });

        matriculation.error = true;
        matriculation.errorMessage = backendResponseCodes.Error.BP
          .BpNotFoundForSap.description.pt_br;
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;

      case (resultError?.body?.code === -18001):
        // "Bp required", -18001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.BP
            .BpRequired.description.pt_br,
        });

        matriculation.error = true;
        matriculation.errorMessage = backendResponseCodes.Error.BP
          .BpRequired.description.pt_br;
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;

      case (resultError?.body?.code === -18002):
        // "MatriculationBpNotFound", -18002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: backendResponseCodes.Error.BP
            .MatriculationBpNotFound.description.pt_br,
        });

        matriculation.error = true;
        matriculation.errorMessage = backendResponseCodes.Error.BP
          .MatriculationBpNotFound.description.pt_br;
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;

      case (resultError?.body?.code === -19000):
        // "SAP timeout", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });

        matriculation.error = true;
        matriculation.errorMessage = t('SAPTimeOut', { ns: namespaces.error.sap });
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;

      case (resultError?.body?.code === -19001):
        // "SAP timeout", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });

        matriculation.error = true;
        matriculation.errorMessage = t('SAPNotFound', { ns: namespaces.error.sap });
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;

      case (resultError?.body?.code === -19002):
        // "SAP timeout", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });

        matriculation.error = true;
        matriculation.errorMessage = t('SAPBadRequest', { ns: namespaces.error.sap });
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        // generic
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Ocorreu um erro ao validar o BP.',
        });

        matriculation.error = true;
        matriculation.errorMessage = 'Ocorreu um erro ao validar o BP.';
        matriculation.status = 'error';
        matriculation.value = value;

        setValidating(false);
        setMatriculation(matriculation);
        break;
    }
  };

  const changeValue = async (text: string): Promise<void> => {
    const newMatriculation: InternalMatriculation = matriculation;

    newMatriculation.error = false;
    newMatriculation.status = '';
    newMatriculation.errorMessage = '';

    setMatriculation(newMatriculation);

    const selectedBranch = formInstance.getFieldValue('branchCoopercitrus');
    const textMatriculation = formInstance.getFieldValue('matricula');

    newMatriculation.value = (text !== '' ? text.replace(/\D/g, '') : textMatriculation.replace(/\D/g, ''));

    if (
      (text.length === 10 && selectedBranch !== undefined && selectedBranch !== 0)
      || (textMatriculation.length === 10 && selectedBranch !== undefined && selectedBranch !== 0)
    ) {
      setMatriculation(newMatriculation);
      setValidating(true);
      setMatriculation(newMatriculation);

      if (text !== '') {
        await validateMatriculation(text);
      } else {
        await validateMatriculation(textMatriculation);
      }
    } else {
      newMatriculation.error = false;
      newMatriculation.errorMessage = '';
      newMatriculation.status = '';
    }

    setMatriculation(newMatriculation);
  };

  const updateSelect = (value: number): void => {
    const { types }: { types: Array<ServiceTypeFromList> } = params.selectedService;
    const type = value;
    const selected = types.find((v) => v.id === type);

    setSelectedType(selected);
  };

  const handleSubmit = async (
    values: {
      type: number | null,
      Subtype: number | null,
    },
  ): Promise<void> => {
    let responseTypeDynamicFields: ResponseWrapper | undefined;
    let responseSubTypeDynamicFields: ResponseWrapper | undefined;
    let subTypeGenericError: Error | undefined;
    let typeGenericError: Error | undefined;

    if (!selectedProperty) {
      await changeValue(formInstance.getFieldValue('matricula'));
      setSelectedPropertyError(true);
      return;
    }

    switch (true) {
      case (values.Subtype != null): {
        const requestSubtypeReference: RequestWrapper = get({
          url: `/service/subtypeDynamicField/${values.Subtype}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        if (subtypeDynamicFieldRequest.current) {
          subtypeDynamicFieldRequest.current.abort();
        }

        subtypeDynamicFieldRequest.current = requestSubtypeReference;

        try {
          responseSubTypeDynamicFields = await requestSubtypeReference;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            console.log('error.response > ', error.response);
          } else {
            subTypeGenericError = error as Error;
          }

          if (!(error instanceof SessionExpiredException)) {
            message.error('Ocorreu um erro ao buscar os campos do serviço, tente novamente');
          }

          return;
        }
      } break;

      case (values.type != null): {
        const requestTypeReference: RequestWrapper = get({
          url: `/service/typeDynamicField/${values.type}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        if (typeDynamicFieldRequest.current) {
          typeDynamicFieldRequest.current.abort();
        }

        typeDynamicFieldRequest.current = requestTypeReference;

        try {
          responseTypeDynamicFields = await requestTypeReference;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            console.log('error.response >', error.response);
          } else {
            typeGenericError = error as Error;
          }

          if (!(error instanceof SessionExpiredException)) {
            message.error('Ocorreu um erro ao buscar os campos do serviço, tente novamente');
          }

          return;
        }
      } break;

      case (subTypeGenericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        break;
    }

    if (values.type) {
      if (
        (values.Subtype && !responseSubTypeDynamicFields?.body?.error)
        || (values.type && !responseTypeDynamicFields?.body?.error)
      ) {
        let serviceTypeDynamicFields = [];
        let serviceSubtypeDynamicFields = [];

        if (values.Subtype) {
          serviceSubtypeDynamicFields = responseSubTypeDynamicFields
            ?.body
            ?.serviceSubtypeDynamicFields || [];
        }

        if (values.type) {
          serviceTypeDynamicFields = responseTypeDynamicFields
            ?.body
            ?.serviceTypeDynamicFields || [];
        }

        const { types } = params.selectedService;
        const subType = formInstance.getFieldValue('Subtype');

        const targetSelectedType = types.find((v) => v.id === values.type);
        const selectedSubType = targetSelectedType?.subtypes
          ? targetSelectedType.subtypes.find((v) => v.id === subType)
          : undefined;

        if (params.selectedService.idCode === 'SPRAY_DRONE') {
          if (targetSelectedType?.idCode === 'SPRAY_DRONE-APLICACAO_AREA_CATACAO') {
            if (selectedSubType?.idCode === 'SPRAY_DRONE-APLICACAO_AREA_CATACAO-SEM_MAPA_ERVA_DANINHA') {
              notification.warning({
                message: 'AVISO',
                description: 'Não é possível realizar esse serviço sem mapeamento. Procure o serviço de mapeamento Coopercitrus.',
              });
              return;
            }
          }
        }

        if (formInstance.getFieldValue('branchCoopercitrus') === 0) {
          return;
        }

        const targetBranchName = (
          params.branchCoopercitrusId === 0
            ? branchsCoopercitrus.find((i) => {
              return i.id === formInstance.getFieldValue('branchCoopercitrus');
            })
            : params.branchName
        );

        const newParameters = {
          serviceTypeDynamicFields,
          serviceSubtypeDynamicFields,
          selectedProperty,
          selectedOwner,
          selectedType: targetSelectedType,
          selectedSubType,
          haveCtc: (getUserType() === 'COOPERCITRUS' ? true : params.haveCtc),
          ctc: params.ctc,
          branchCoopercitrusId: (
            params.branchCoopercitrusId === 0
              ? formInstance.getFieldValue('branchCoopercitrus')
              : params.branchCoopercitrusId
          ),
          branchName: (targetBranchName?.name ? targetBranchName.name : params.branchName),
          choiceServiceType: params.choiceServiceType,
        };

        if (selectedMatriculation !== 'COOPERATIVE') {
          newParameters.matriculation = selectedMatriculation;
        }

        nextPage(newParameters);
      } else {
        message.error('Ocorreu um erro ao buscar os campos do serviço, tente novamente');
      }
    } else {
      const targetBranchName = (
        params.branchCoopercitrusId === 0
          ? branchsCoopercitrus.find((i) => {
            return i.id === formInstance.getFieldValue('branchCoopercitrus');
          })
          : params.branchName
      );

      if (formInstance.getFieldValue('branchCoopercitrus') === 0) {
        return;
      }

      const newParameters = {
        serviceTypeDynamicFields: [],
        serviceSubtypeDynamicFields: [],
        selectedProperty,
        selectedOwner,
        selectedType: { id: 0, name: '', subtypes: [] },
        haveCtc: (getUserType() === 'COOPERCITRUS' ? true : params.haveCtc),
        ctc: params.ctc,
        branchCoopercitrusId: (
          params.branchCoopercitrusId === 0
            ? formInstance.getFieldValue('branchCoopercitrus')
            : params.branchCoopercitrusId
        ),
        branchName: (targetBranchName?.name ? targetBranchName.name : params.branchName),
        choiceServiceType: params.choiceServiceType,
      };

      if (selectedMatriculation !== 'COOPERATIVE') {
        newParameters.matriculation = selectedMatriculation;
      }
      nextPage(newParameters);
    }
  };

  useEffect(() => {
    if (branchsCoopercitrus.length > 0) {
      if (params.branchCoopercitrusId > 0) {
        formInstance.setFieldsValue({
          matricula: params.selectedMatriculation,
          branchCoopercitrus: params.branchCoopercitrusId,
        });
      }
    }
  }, [branchsCoopercitrus]);

  useEffect(() => {
    if (getUserType() === 'COOPERATIVE' || selectedMatriculation !== null) {
      getMyProperties();
      getListAllBranchs();
    } else {
      getListAllBranchs();

      if (params.ctc === '') {
        params.ctc = userName;
      }
    }

    return () => {
      setLoading(false);

      if (branchesRequest.current) {
        branchesRequest.current.abort();
        branchesRequest.current = undefined;
      }

      if (matriculationsWithLandsRequest.current) {
        matriculationsWithLandsRequest.current.abort();
        matriculationsWithLandsRequest.current = undefined;
      }

      if (updateDataFromSAPRequest.current) {
        updateDataFromSAPRequest.current.abort();
        updateDataFromSAPRequest.current = undefined;
      }

      if (retrieveLandOwnersByLandRequest.current) {
        retrieveLandOwnersByLandRequest.current.abort();
        retrieveLandOwnersByLandRequest.current = undefined;
      }

      if (myPropertiesRequest.current) {
        myPropertiesRequest.current.abort();
        myPropertiesRequest.current = undefined;
      }

      if (validateMatriculationRequest.current) {
        validateMatriculationRequest.current.abort();
        validateMatriculationRequest.current = undefined;
      }

      if (subtypeDynamicFieldRequest.current) {
        subtypeDynamicFieldRequest.current.abort();
        subtypeDynamicFieldRequest.current = undefined;
      }

      if (typeDynamicFieldRequest.current) {
        typeDynamicFieldRequest.current.abort();
        typeDynamicFieldRequest.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedMatriculation != null) {
      getMyProperties();
    }
  }, [selectedMatriculation]);

  return (
    <div className="SelectPropertyData">
      <Spin
        tip="Carregando propriedades..."
        spinning={loading}
      >
        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          onValuesChange={
            (changedValues): void => {
              if ('branchCoopercitrus' in changedValues) {
                updateParams({
                  branchCoopercitrusId: formInstance.getFieldValue('branchCoopercitrus'),
                  branchName: branchsCoopercitrus.find((i) => {
                    return i.id === formInstance.getFieldValue('branchCoopercitrus');
                  })?.name,
                });
              }
            }
          }
          onFinish={handleSubmit}
          onFinishFailed={
            (errorInfo): void => {
              console.log('onFinishFailed', errorInfo);
            }
          }
        >
          <Row gutter={24}>
            {
              !selectedMatriculation
                ? (
                  <>
                    <Col xs={{ span: 24 }} lg={{ span: 13 }}>
                      <div className="select-property-text">
                        <span className="required-in">*</span>
                        Insira um BP
                      </div>
                      <div className="matriculationSelect">
                        <Form.Item
                          validateStatus={(!matriculation || matriculation.error) ? 'error' : ''}
                          help={!matriculation ? '' : (matriculation.error && matriculation.errorMessage)}
                          className="codes"
                        >
                          <Form.Item
                            name="matricula"
                            noStyle
                          >
                            <Input
                              value={matriculation.value}
                              onChange={
                                (text): void => {
                                  changeValue(text.target.value);
                                }
                              }
                              suffix={
                                !validating
                                  ? getItemIcon(matriculation.status)
                                  : <Spin />
                              }
                              onBlur={
                                (eventt): void => {
                                  const newvalue = eventt.target.value.replace(/.*/, formatBP(eventt.target.value));
                                  formInstance.setFieldsValue({
                                    matricula: newvalue,
                                  });
                                  changeValue(newvalue);
                                }
                              }
                              type="number"
                              className="input-registration"
                              placeholder="BP com 10 dígitos"
                              maxLength={10}
                            />
                          </Form.Item>
                          {
                            selectedPropertyError
                            && (
                              <div
                                className="ant-form-explain"
                                style={{ color: '#f5222d', paddingBottom: 20 }}
                              >
                                Por favor, digite um bp válido.
                              </div>
                            )
                          }
                        </Form.Item>
                      </div>
                    </Col>

                    <Col xs={{ span: 24 }} lg={{ span: 13 }}>
                      <Form.Item
                        label={(
                          <span className="select-property-text">
                            {
                              userType === 'COOPERCITRUS'
                                ? 'Selecione a filial Coopercitrus'
                                : 'Por qual filial Coopercitrus é atendido(a)?'
                            }
                          </span>
                        )}
                        name="branchCoopercitrus"
                        rules={[
                          { required: true, message: 'Por favor, selecione a filial' },
                        ]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="children"
                          onChange={
                            (): void => {
                              changeValue('');
                            }
                          }
                        >
                          {
                            branchsCoopercitrus.map((v) => (
                              <Select.Option key={v.id} value={v.id}>
                                {v.name}
                              </Select.Option>
                            ))
                          }
                        </Select>
                      </Form.Item>
                    </Col>
                  </>
                )
                : (
                  <Col>
                    <div
                      style={
                        selectedMatriculation === 'COOPERATIVE'
                          ? { display: 'none' }
                          : undefined
                      }
                    >
                      <h3>
                        <span className="select-property-text">BP selecionado:</span>
                        {` ${selectedMatriculation} - ${userType === 'COOPERCITRUS' ? matriculation.cooperadoName : userName}`}
                        <EditOutlined
                          onClick={
                            (): void => {
                              setSelectedMatriculation(null);
                              setSelectedProperty(null);
                              setSelectedOwner(null);
                              setSelectedPropertyError(false);
                              setMatriculation({ ...matriculation, value: '', status: '' });
                              params.branchCoopercitrusId = 0;
                            }
                          }
                        />
                      </h3>
                    </div>
                  </Col>
                )
            }
          </Row>
          <Row gutter={24}>
            <Col xs={{ span: 24 }} lg={{ span: 16 }}>
              <Row>
                {
                  selectedMatriculation
                  && (!selectedProperty
                    ? (
                      <Col span={24}>
                        <div>
                          {/* nest form items */}
                          <Form.Item
                            label={<span className="select-property-text">Selecione uma propriedade</span>}
                            style={{ marginTop: '10px' }}
                            required
                            name="propriedades"
                          >
                            <Row gutter={24}>
                              <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                                <div>
                                  <div className="property-land-title">
                                    Propriedades Coopercitrus
                                    <DownOutlined />
                                  </div>
                                  <div className="select-property-container">
                                    {
                                      cooperProperties.length
                                        ? cooperProperties.map(
                                          (v) => (
                                            <div
                                              key={v.id}
                                              role="button"
                                              onClick={
                                                (): void => {
                                                  selectProperty(v.id, cooperProperties);
                                                }
                                              }
                                              onKeyDown={
                                                (eventt): void => {
                                                  if (eventt.key === 'Enter') {
                                                    selectProperty(v.id, cooperProperties);
                                                  }
                                                }
                                              }
                                              tabIndex={0}
                                              className="property-item"
                                            >
                                              {v.name}
                                            </div>
                                          ),
                                        )
                                        : (
                                          <div className="no-items-found">
                                            Nenhuma propriedade encontrada
                                          </div>
                                        )
                                    }
                                  </div>
                                </div>
                              </Col>
                              <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                                <div>
                                  <div className="property-land-title">
                                    Outras Propriedades
                                    <DownOutlined />
                                  </div>
                                  <div className="select-property-container">
                                    {
                                      otherProperties.length
                                        ? otherProperties.map(
                                          (v) => (
                                            <div
                                              key={v.id}
                                              role="button"
                                              onClick={
                                                (): void => {
                                                  selectProperty(v.id, otherProperties);
                                                }
                                              }
                                              onKeyDown={
                                                (eventt): void => {
                                                  if (eventt.key === 'Enter') {
                                                    selectProperty(v.id, otherProperties);
                                                  }
                                                }
                                              }
                                              tabIndex={0}
                                              className="property-item"
                                            >
                                              {v.name}
                                            </div>
                                          ),
                                        )
                                        : (
                                          <div className="no-items-found">
                                            Nenhuma propriedade encontrada
                                          </div>
                                        )
                                    }
                                    <Button
                                      onClick={
                                        (): void => {
                                          nextPage({
                                            isAddingLand: true,
                                            selectedMatriculation,
                                          });
                                        }
                                      }
                                      className="add-land-button"
                                    >
                                      Adicionar propriedade
                                    </Button>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </Form.Item>
                        </div>
                      </Col>
                    )
                    : (
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 12 }}
                        style={{ paddingLeft: '0px' }}
                      >
                        {/* nest */}
                        <Form.Item
                          label={<span className="select-property-text">Propriedade selecionada</span>}
                          style={{ marginTop: '10px' }}
                          required
                          name="propriedade"
                        >
                          <h3 className="selected-property-text">
                            Propriedade:&nbsp;
                            {selectedProperty.name}
                            <EditOutlined
                              onClick={
                                (): void => {
                                  setSelectedProperty(null);
                                  setSelectedOwner(null);
                                }
                              }
                            />
                          </h3>
                        </Form.Item>
                      </Col>
                    )
                  )
                }
                {
                  selectedPropertyError
                  && selectedMatriculation
                  && (
                    <div
                      className="ant-form-explain"
                      style={{ color: '#f5222d', paddingBottom: 20 }}
                    >
                      {t('select_property', { ns: namespaces.pages.service.serviceComponents.selectPropertyData.selectPropertyData })}
                    </div>
                  )
                }
                {
                  selectedProperty
                  && (
                    <Col xs={{ span: 24 }} lg={{ span: 12 }} style={{ marginTop: 10 }}>
                      <Form.Item
                        label={<span className="select-property-text">{t('responsible_cooperative', { ns: namespaces.pages.service.serviceComponents.selectPropertyData.selectPropertyData })}</span>}
                        name="owner"
                        rules={[
                          {
                            required: true,
                            message: t('please_select_proprietary', { ns: namespaces.pages.service.serviceComponents.selectPropertyData.selectPropertyData }),
                          },
                        ]}
                      >
                        <Select
                          onChange={selectOwner}
                          placeholder="Selecione o responsável"
                        >
                          {
                            owners.map((v) => (
                              <Select.Option
                                key={v.id}
                                value={v.id}
                                disabled={v.disabled}
                              >
                                {v.name}
                              </Select.Option>
                            ))
                          }
                        </Select>
                      </Form.Item>
                    </Col>
                  )
                }
              </Row>
            </Col>
            {
              selectedPropertyMatriculation != null
              && getUserType() !== 'COOPERCITRUS'
              && (
                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 12 }}
                >
                  <Row>
                    <span className="select-property-text">
                      {`Matrícula selecionada: ${selectedPropertyMatriculation}`}
                    </span>
                  </Row>
                </Col>
              )
            }
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              {
                !selectedProperty
                && selectedMatriculation
                && (
                  <Row>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Button
                        icon={<RefreshIcon />}
                        className="refresh-link"
                        type="link"
                        tabIndex={0}
                        onClick={refreshProperties}
                      >
                        Atualizar
                      </Button>
                    </Col>
                  </Row>
                )
              }
            </Col>
          </Row>
          {
            !!params.selectedService.types.length
            && selectedMatriculation
            && (
              <Row gutter={24}>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label={<span className="select-property-text">Tipo</span>}
                    name="type"
                    rules={[
                      { required: true, message: 'Por favor, selecione o tipo' },
                    ]}
                  >
                    <Select
                      onChange={
                        (value): void => {
                          updateSelect(value);
                        }
                      }
                      placeholder="Selecione o tipo"
                    >
                      {
                        params.selectedService.types.map((v: ServiceTypeFromList) => (
                          <Select.Option key={v.id} value={v.id}>
                            {v.name}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )
          }
          {
            !!params.selectedService.types.length
            && selectedMatriculation
            && selectedType != null
            && selectedType.subtypes.length > 0
            && (
              <Row gutter={24}>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label={<span className="select-property-text">Subtipo</span>}
                    name="Subtype"
                    rules={[
                      { required: true, message: 'Por favor, selecione o Subtipo' },
                    ]}
                  >
                    <Select
                      placeholder="Selecione o Subtipo"
                    >
                      {
                        selectedType.subtypes.map((v) => (
                          <Select.Option key={v.id} value={v.id}>
                            {v.name}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )
          }
          <div className="controls-container">
            <Form.Item
              noStyle
            >
              <Button
                size="large"
                className="btn-previus-step"
                onClick={
                  (): void => {
                    previousPage({
                      selectedService: {
                        id: 0,
                        name: '',
                        description: '',
                        types: [],
                      },
                    });
                  }
                }
              >
                Cancelar
              </Button>
            </Form.Item>
            <Form.Item
              noStyle
            >
              <Button
                size="large"
                type="primary"
                htmlType="submit"
              >
                Prosseguir
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

SelectPropertyData.propTypes = {
  previousPage: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  updateParams: PropTypes.func.isRequired,

  params: PropTypes.shape({
    selectedService: PropTypes.objectOf(PropTypes.any).isRequired,
    service: PropTypes.string,
    selectedMatriculation: PropTypes.string,
    createdLandId: PropTypes.number,
    haveCtc: PropTypes.bool,
    choiceServiceType: PropTypes.number,
    branchName: PropTypes.string,
    branchCoopercitrusId: PropTypes.number,
    ctc: PropTypes.string,
  }).isRequired,
};

SelectPropertyData.defaultProps = {};

export default SelectPropertyData;
